import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { ValidatorForm } from "react-material-ui-form-validator";
import axios from "../../../axios-order";
import Modal from "react-bootstrap/Modal";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import DescriptionIcon from "@mui/icons-material/Description";
import Tooltip from "@mui/material/Tooltip";
import SelectField from "../../../components/ui/basicSelectInput";
import { canRead, canWrite } from "../../../utils/privilegeUtils";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
    navy: "#1a3f6f",
    accent: "#1a6fc4",
    accentLight: "#e8f1fb",
    white: "#ffffff",
    surface: "#f7f9fc",
    border: "#dde3ec",
    borderLight: "#eef1f6",
    text: "#1a3f6f",
    textSub: "#4a6080",
    textMuted: "#8298b2",
    success: { bg: "#edf8f1", text: "#1a6e3e", dot: "#28a55f" },
    warning: { bg: "#fff8e6", text: "#7a4e00", dot: "#f0a500" },
    info: { bg: "#e8f1fb", text: "#1a4a8a", dot: "#1a6fc4" },
};

const monthArray = [
    { id: 4, value: "April" },
    { id: 5, value: "May" },
    { id: 6, value: "June" },
    { id: 7, value: "July" },
    { id: 8, value: "August" },
    { id: 9, value: "September" },
    { id: 10, value: "October" },
    { id: 11, value: "November" },
    { id: 12, value: "December" },
    { id: 1, value: "January" },
    { id: 2, value: "February" },
    { id: 3, value: "March" },
];

function formatDateTime(dt) {
    if (!dt) return "—";
    const cleaned = dt.replace("Z", "");
    const parts = cleaned.includes("T") ? cleaned.split("T") : cleaned.split(" ");
    const date = parts[0]; const time = parts[1] || "";
    return (
        <span>
            <span style={{ color: C.text, fontWeight: "500" }}>{date}</span>
            {time && <span style={{ color: C.textMuted }}> · {time}</span>}
        </span>
    );
}

const DocIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="13" x2="15" y2="13" />
        <line x1="9" y1="17" x2="13" y2="17" />
    </svg>
);

// ── Component ─────────────────────────────────────────────────────────────────
export default function TdsReport() {
    const [handleFromMonth, setHandleFromMonth] = useState("");
    const [handleToMonth, setHandleToMonth] = useState("");
    const [handleFinancialYear, setHandleFinancialYear] = useState("");
    const [trigarLoader, setTrigarLoader] = useState(false);
    const [trigarForErrorMessage, setTrigarForErrorMessage] = useState(false);
    const [triggerModelFromToDate, setTriggerModelFromToDate] = useState(false);
    const [tdsReconHistoryData, setTdsReconHistoryData] = useState([]);
    const [loaderForTable, setLoaderForTable] = useState(false);
    const [filteredToMonths, setFilteredToMonths] = useState(monthArray);
    const [yearFromArr, setFromYearArr] = useState([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [message] = useState("");
    const [responseMessage, setResponseMessage] = useState("");
    const [hoveredRow, setHoveredRow] = useState(null);

    const [excelLoadingId, setExcelLoadingId] = useState(null);

    const history = useHistory();
    const handleBack = () => history.push("/reportCards");

    // ── Year values ───────────────────────────────────────────────────────────
    const setFromYearValues = () => {
        let current_year = new Date().getFullYear();
        let yearArr = [];
        for (let i = current_year - 10; i <= current_year; i++) {
            const toYear = String(i + 1);
            yearArr.push({ id: i, year: `${i}-${toYear.slice(2)}` });
        }
        setFromYearArr(yearArr);
    };

    // ── Month handlers ────────────────────────────────────────────────────────
    const handleFromMonthChange = (e) => {
        const selectedFrom = e.target.value;
        setHandleFromMonth(selectedFrom);
        setHandleToMonth("");
        const fromMonthIndex = monthArray.findIndex((month) => month.value === selectedFrom);
        setFilteredToMonths(monthArray.slice(fromMonthIndex));
    };
    const handleToMonthChange = (e) => setHandleToMonth(e.target.value);
    const handleYearChange = (e) => setHandleFinancialYear(e.target.value);

    // ── Snackbar ──────────────────────────────────────────────────────────────
    const handleSnackbarOpen = (msg, severity) => {
        setSnackbarOpen(true); setSnackbarSeverity(severity); setResponseMessage(msg);
    };
    const handleSnackbarClose = () => {
        setSnackbarOpen(false); setSnackbarSeverity("success"); setResponseMessage("");
    };

    // ── Data fetch ────────────────────────────────────────────────────────────
    const getTdsReportLogs = () => {
        const business_entity_id = localStorage.getItem("business_entity_id");
        const report_type = "tds_report";
        setLoaderForTable(true);
        axios
            .get(`/arc/log_details_tds_report/${business_entity_id}/${report_type}`)
            .then((res) => {
                setLoaderForTable(false);
                setTdsReconHistoryData(res.data?.records?.length > 0 ? res.data.records : []);
            })
            .catch((err) => {
                setLoaderForTable(false);
                console.error("Error fetching logs:", err);
                setTdsReconHistoryData([]);
            });
    };

    useEffect(() => { getTdsReportLogs(); setFromYearValues(); }, []);

    // ── Modal close ───────────────────────────────────────────────────────────
    const handleOpenAndCloseModel = () => {
        setHandleFromMonth(""); setHandleToMonth(""); setHandleFinancialYear("");
        setFilteredToMonths(monthArray);
        setTriggerModelFromToDate(false);
        setTrigarLoader(false); setTrigarForErrorMessage(false);
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const onSubmitHandlerForData = (event) => {
        event.preventDefault();
        if (!handleFromMonth || !handleToMonth || !handleFinancialYear) {
            handleSnackbarOpen("Please fill all required fields", "error"); return;
        }
        const payLoadData = {
            business_entity_id: parseInt(localStorage.getItem("business_entity_id")),
            from_month: handleFromMonth, to_month: handleToMonth,
            financial_year: handleFinancialYear,
            org_gstin: localStorage.getItem("gst"),
            report_type: "tds_report",
        };
        axios.post("/arc/tds_appliacbility", payLoadData)
            .then((res) => { handleSnackbarOpen(res.data.message || "Report generated successfully", "success"); getTdsReportLogs(); })
            .catch((err) => { handleSnackbarOpen(err.response?.data?.message || "Error generating report", "error"); });
        handleOpenAndCloseModel();
    };

    // ── Download ──────────────────────────────────────────────────────────────
    const downloadBase64File = (contentType, base64Data, fileName) => {
        const linkSource = `data:${contentType};base64,${base64Data}`;
        const downloadLink = document.createElement("a");
        downloadLink.href = linkSource; downloadLink.download = fileName; downloadLink.click();
    };

    const downloadExcelReport = (id) => {
        setExcelLoadingId(id);
        const contentType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        axios.get(`/arc/generate_excel_report_tds_report/${id}`)
            .then((res) => { downloadBase64File(contentType, res.data.file, "TDS_Report.xlsx"); handleSnackbarOpen("Report downloaded successfully", "success"); })
            .catch((err) => { console.error("Error downloading report:", err); handleSnackbarOpen("Error downloading report", "error"); })
            .finally(() => setExcelLoadingId(null));
    };

    // const downloadExcelReport = (id) => {
    //     const contentType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    //     axios.get(`/arc/generate_excel_report_tds_report/${id}`)
    //         .then((res) => { downloadBase64File(contentType, res.data.file, "TDS_Report.xlsx"); handleSnackbarOpen("Report downloaded successfully", "success"); })
    //         .catch((err) => { console.error("Error downloading report:", err); handleSnackbarOpen("Error downloading report", "error"); });
    // };

    // ── Refresh ───────────────────────────────────────────────────────────────
    const handleRefreshLogs = () => {
        const business_entity_id = localStorage.getItem("business_entity_id");
        setLoaderForTable(true);
        axios.get(`/arc/refresh_tds_logs/${business_entity_id}`)
            .then(() => { handleSnackbarOpen("Logs refreshed", "success"); getTdsReportLogs(); })
            .catch(() => { setLoaderForTable(false); handleSnackbarOpen("Error refreshing logs", "error"); });
    };

    // ── Status badge ──────────────────────────────────────────────────────────
    const StatusBadge = ({ status }) => {
        const s = status || "Pending";
        const scheme = s === "Pass" ? C.success : s === "Completed" ? C.info : C.warning;
        return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "3px 10px", borderRadius: "5px", fontSize: "11px", fontWeight: "600", background: scheme.bg, color: scheme.text }}>
                <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: scheme.dot, flexShrink: 0 }} />
                {s}
            </span>
        );
    };

    // ── Styles ────────────────────────────────────────────────────────────────
    const thStyle = {
        padding: "11px 18px", textAlign: "left", fontSize: "11px", fontWeight: "600",
        letterSpacing: "0.05em", color: "#ffffff", background: C.navy,
        borderBottom: "1px solid #16355c", textTransform: "uppercase", whiteSpace: "nowrap",
    };
    const tdStyle = {
        padding: "8px 18px", fontSize: "13px", color: C.text,
        borderBottom: `1px solid ${C.borderLight}`,
    };
    const labelStyle = {
        fontSize: "12px", fontWeight: "600", color: C.textSub,
        marginBottom: "6px", display: "block",
        textTransform: "uppercase", letterSpacing: "0.05em",
    };

    // ── Table body ────────────────────────────────────────────────────────────
    const renderTableBody = () => {
        if (loaderForTable) {
            return Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                    {[15, 15, 15, 25, 15, 10].map((w, j) => (
                        <td key={j} style={tdStyle}>
                            <div style={{ height: "14px", borderRadius: "4px", background: C.borderLight, width: `${w}%`, animation: "pulse 1.4s ease-in-out infinite" }} />
                        </td>
                    ))}
                </tr>
            ));
        }
        if (!tdsReconHistoryData || tdsReconHistoryData.length === 0) {
            return (
                <tr>
                    <td colSpan={6} style={{ ...tdStyle, textAlign: "center", padding: "52px 20px" }}>
                        <div style={{ fontSize: "28px", marginBottom: "10px", opacity: 0.4 }}>📄</div>
                        <div style={{ fontWeight: "500", marginBottom: "4px", color: C.textSub }}>No reports found</div>
                        <div style={{ fontSize: "12px", color: C.textMuted }}>Generate your first TDS Applicability report to get started.</div>
                    </td>
                </tr>
            );
        }
        return tdsReconHistoryData.map((item, i) => (
            <tr
                key={i}
                onMouseEnter={() => setHoveredRow(i)}
                onMouseLeave={() => setHoveredRow(null)}
                style={{ background: hoveredRow === i ? C.surface : C.white, transition: "background 0.1s" }}
            >
                <td style={tdStyle}><span style={{ fontWeight: "600" }}>{item.from_month || "N/A"}</span></td>
                <td style={tdStyle}><span style={{ fontWeight: "600" }}>{item.to_month || "N/A"}</span></td>
                <td style={tdStyle}><span style={{ fontWeight: "600" }}>{item.financial_year || "N/A"}</span></td>
                <td style={tdStyle}>
                    <span style={{ fontSize: "12px", color: C.textMuted, fontVariantNumeric: "tabular-nums" }}>
                        {formatDateTime(item.created_date_time)}
                    </span>
                </td>
                <td style={tdStyle}><StatusBadge status={item.status} /></td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
    {(() => {
        const isDownloadable = item.status === "Pass";
        return (
            <Tooltip title={isDownloadable ? "Download Excel Report" : "Report not ready for download"} arrow>
                <span style={{ display: "inline-flex" }}>
                    {excelLoadingId === item.id ? (
                        <div
                            style={{
                                width: "18px",
                                height: "18px",
                                border: `2px solid ${C.border}`,
                                borderTopColor: C.accent,
                                borderRadius: "50%",
                                animation: "spin 0.7s linear infinite",
                            }}
                        />
                    ) : (
                        <DescriptionIcon
                            onClick={() => isDownloadable && downloadExcelReport(item.id)}
                            style={{
                                cursor: isDownloadable ? "pointer" : "not-allowed",
                                color: isDownloadable ? "#1d6f42" : C.textMuted,
                                fontSize: "26px",
                                opacity: isDownloadable ? 1 : 0.4,
                                pointerEvents: isDownloadable ? "auto" : "none",
                            }}
                        />
                    )}
                </span>
            </Tooltip>
        );
    })()}
</td>
                {/* <td style={{ ...tdStyle, textAlign: "center" }}>
                    <Tooltip title="Download Excel Report" arrow>
                        <span style={{ display: "inline-flex" }}>
                            <DescriptionIcon
                                className="action-icon-button showIcon"
                                onClick={() => downloadExcelReport(item.id)}
                                style={{ cursor: "pointer", color: "#1d6f42", fontSize: "26px" }}
                            />
                        </span>
                    </Tooltip>
                </td> */}
            </tr>
        ));
    };

    const totalCount = tdsReconHistoryData ? tdsReconHistoryData.length : 0;
    const completedCount = tdsReconHistoryData ? tdsReconHistoryData.filter((r) => r.status === "Pass" || r.status === "Completed").length : 0;
    const fyCount = tdsReconHistoryData ? new Set(tdsReconHistoryData.map((r) => r.financial_year)).size : 0;

    return (
        <React.Fragment>
            <style>{`
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
  @keyframes spin { to { transform: rotate(360deg); } }
`}</style>

            <div style={{ padding: "28px 32px", background: C.surface, fontFamily: "inherit" }}>

                {/* ── Page header ── */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: C.white, border: `1px solid ${C.border}`, borderRadius: "10px", padding: "16px 20px", marginBottom: "20px", boxShadow: "0 1px 3px rgba(13,33,55,0.06)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                        <button
                            onClick={handleBack}
                            title="Back to Reports"
                            style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.white, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.textSub, fontSize: "14px", flexShrink: 0 }}
                        >←</button>
                        <div style={{ width: "42px", height: "42px", borderRadius: "10px", background: C.accentLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <DocIcon />
                        </div>
                        <div>
                            <div style={{ fontSize: "23px", fontWeight: "700", color: C.text, lineHeight: 1.2 }}>TDS Applicability Report</div>
                            <div style={{ fontSize: "12px", color: C.textMuted, marginTop: "4px" }}>TDS applicability summary records</div>
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                        <button
                            onClick={handleRefreshLogs}
                            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "0 16px", height: "36px", borderRadius: "8px", background: C.white, color: C.textSub, border: `1px solid ${C.border}`, fontSize: "13px", fontWeight: "500", cursor: "pointer" }}
                        >
                            ↻ Refresh
                        </button>
                       {canWrite("TDS APPLICABILITY REPORT") && (
                        <button
                            onClick={() => setTriggerModelFromToDate(true)}
                            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "0 18px", height: "36px", borderRadius: "8px", background: C.navy, color: C.white, border: "none", fontSize: "13px", fontWeight: "500", cursor: "pointer" }}
                        >
                            <span style={{ fontSize: "16px", lineHeight: 1 }}>+</span>
                            New TDS Applicability Report
                        </button>
                        )}
                    </div>
                </div>

                {/* ── Metrics cards ── */}
                {/* <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px", marginBottom: "20px" }}>
                    {[
                        { label: "Total Reports",    value: totalCount,     borderColor: C.accent,  badge: totalCount > 0 },
                        { label: "Completed",        value: completedCount, borderColor: "#28a55f", badge: false },
                        { label: "Financial Years",  value: fyCount,        borderColor: "#8298b2", badge: false },
                    ].map(({ label, value, borderColor, badge }) => (
                        <div key={label} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "10px", padding: "16px 20px", boxShadow: "0 1px 3px rgba(13,33,55,0.05)", borderTop: `3px solid ${borderColor}` }}>
                            <div style={{ fontSize: "26px", fontWeight: "700", color: C.text, lineHeight: 1, letterSpacing: "-0.5px" }}>{value}</div>
                            <div style={{ fontSize: "11px", color: C.textMuted, marginTop: "5px", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
                            {badge && <div style={{ display: "inline-block", marginTop: "8px", fontSize: "10px", padding: "2px 8px", borderRadius: "12px", background: C.success.bg, color: C.success.text, fontWeight: "600" }}>All generated</div>}
                        </div>
                    ))}
                </div> */}

                {/* ── Table card ── */}
                <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "10px", overflow: "hidden", boxShadow: "0 1px 3px rgba(13,33,55,0.05)" }}>
                    <div style={{ maxHeight: "480px", overflowY: "auto", overflowX: "hidden" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                            <colgroup>
                                <col style={{ width: "14%" }} />
                                <col style={{ width: "14%" }} />
                                <col style={{ width: "14%" }} />
                                <col style={{ width: "28%" }} />
                                <col style={{ width: "18%" }} />
                                <col style={{ width: "12%" }} />
                            </colgroup>
                            <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                <tr>
                                    <th style={thStyle}>From Month</th>
                                    <th style={thStyle}>To Month</th>
                                    <th style={thStyle}>Financial Year</th>
                                    <th style={thStyle}>Run Date &amp; Time</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={{ ...thStyle, textAlign: "center" }}>Download</th>
                                </tr>
                            </thead>
                            <tbody>{renderTableBody()}</tbody>
                        </table>
                    </div>

                    {/* Footer */}
                    {!loaderForTable && tdsReconHistoryData && tdsReconHistoryData.length > 0 && (
                        <div style={{ padding: "10px 18px", borderTop: `1px solid ${C.borderLight}`, fontSize: "11px", color: C.textMuted, background: C.white }}>
                            Showing {tdsReconHistoryData.length} record{tdsReconHistoryData.length !== 1 ? "s" : ""}
                        </div>
                    )}
                </div>

            </div>

            {/* ── Modal ── */}
            {canWrite("TDS APPLICABILITY REPORT") && <Modal size="lg" centered show={triggerModelFromToDate} onHide={handleOpenAndCloseModel}>
                <Modal.Header closeButton style={{ background: C.navy, borderBottom: "none", padding: "16px 22px" }}>
                    <Modal.Title style={{ fontSize: "18px", fontWeight: "600", color: "#ffffff" }}>
                        TDS Applicability Report
                    </Modal.Title>
                </Modal.Header>

                <ValidatorForm onSubmit={onSubmitHandlerForData} onError={(errors) => console.log(errors)}>
                    <Modal.Body style={{ padding: "22px 22px 10px" }}>

                        {/* Financial Year */}
                        <div style={{ marginBottom: "16px" }}>
                            <label style={labelStyle}>Financial Year <span style={{ color: "#e53e3e" }}>*</span></label>
                            <SelectField
                                validator={["required"]}
                                errorMsg={["This field is required"]}
                                choice_id="year"
                                choice_name="year"
                                name="financial_year"
                                choices={yearFromArr}
                                value={handleFinancialYear}
                                onChange={handleYearChange}
                            />
                        </div>

                        {/* From Month + To Month */}
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" }}>
                            <div>
                                <label style={labelStyle}>From Month <span style={{ color: "#e53e3e" }}>*</span></label>
                                <SelectField
                                    validator={["required"]}
                                    errorMsg={["This field is required"]}
                                    choice_id="value"
                                    choice_name="value"
                                    name="from_month"
                                    choices={monthArray}
                                    value={handleFromMonth}
                                    onChange={handleFromMonthChange}
                                />
                            </div>
                            <div>
                                <label style={labelStyle}>To Month <span style={{ color: "#e53e3e" }}>*</span></label>
                                <SelectField
                                    validator={["required"]}
                                    errorMsg={["This field is required"]}
                                    choice_id="value"
                                    choice_name="value"
                                    name="to_month"
                                    choices={filteredToMonths}
                                    value={handleToMonth}
                                    onChange={handleToMonthChange}
                                />
                            </div>
                        </div>

                        {trigarLoader && (
                            <div style={{ fontWeight: "600", color: "green", padding: "5px" }}>
                                Please wait, report generation is in progress...
                            </div>
                        )}
                        {trigarForErrorMessage && (
                            <div style={{ color: "#e53e3e", fontWeight: "700" }}>No Records Present!</div>
                        )}
                    </Modal.Body>

                    <Modal.Footer style={{ padding: "12px 22px", gap: "8px", borderTop: `1px solid ${C.borderLight}` }}>
                        <button
                            type="button"
                            onClick={handleOpenAndCloseModel}
                            style={{ padding: "0 18px", height: "36px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.white, fontSize: "13px", color: C.textSub, cursor: "pointer" }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={{ padding: "0 22px", height: "36px", borderRadius: "8px", background: C.navy, color: C.white, color: C.white, border: "none", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}
                        >
                            Submit
                        </button>
                    </Modal.Footer>
                </ValidatorForm>
            </Modal>}

            {/* ── Snackbar ── */}
            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={handleSnackbarClose}
                style={{ maxWidth: "340px" }}
            >
                <MuiAlert elevation={6} variant="filled" onClose={handleSnackbarClose} severity={snackbarSeverity}>
                    {responseMessage}{message}
                </MuiAlert>
            </Snackbar>
        </React.Fragment>
    );
}